import { Link } from "@tanstack/react-router";
import { BookHeart } from "lucide-react";

import type { ManualListRow } from "@/api/db/manuals";
import type { ManualListInput } from "@/api/router/manuals/schemas";
import { ToneChip } from "@/components/medico/tone-chip";
import { EmptyState } from "@/components/ui/empty-state";
import { Pager } from "@/components/ui/pager";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "@/lib/format";
import { ManualLabels } from "@/lib/labels/manuals";
import { cn } from "@/lib/utils";

export type ManualFilter = ManualListInput["filter"];

const filters: { id: ManualFilter; label: string }[] = [
	{ id: "all", label: ManualLabels.list.filters.all },
	{ id: "draft", label: ManualLabels.list.filters.draft },
	{ id: "delivered", label: ManualLabels.list.filters.delivered },
	{ id: "not_opened", label: ManualLabels.list.filters.notOpened },
	{ id: "reread", label: ManualLabels.list.filters.reread },
	{ id: "revoked", label: ManualLabels.list.filters.revoked },
];

function FilterSegments({
	filter,
	onFilter,
}: {
	filter: ManualFilter;
	onFilter: (filter: ManualFilter) => void;
}) {
	return (
		<div className="max-w-full overflow-x-auto">
			<div className="inline-flex items-center gap-0.5 rounded-full bg-secondary p-0.5">
				{filters.map(({ id, label }) => (
					<button
						key={id}
						type="button"
						data-slot={`manual-filter-${id}`}
						aria-pressed={filter === id}
						onClick={() => onFilter(id)}
						className={cn(
							"whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors",
							"focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
							filter === id
								? "bg-card font-medium text-foreground shadow-control"
								: "text-muted-foreground hover:text-foreground",
						)}
					>
						{label}
					</button>
				))}
			</div>
		</div>
	);
}

function StatusCell({ row }: { row: ManualListRow }) {
	return (
		<span className="flex flex-wrap items-center gap-1.5">
			<ToneChip tone={ManualLabels.statusTone[row.status]}>
				{ManualLabels.status[row.status]}
			</ToneChip>

			{row.orphaned && <ToneChip tone="amber">{ManualLabels.list.orphaned}</ToneChip>}
		</span>
	);
}

function OpenedCell({ row }: { row: ManualListRow }) {
	if (row.deliveredAt === null) {
		return <span className="text-muted-foreground">{ManualLabels.list.none}</span>;
	}

	if (row.firstAccessAt === null) {
		return <span className="text-warning">{ManualLabels.list.notOpened}</span>;
	}

	return (
		<span className="font-mono text-muted-foreground">{format.dateTime(row.firstAccessAt)}</span>
	);
}

function AccessesCell({ row }: { row: ManualListRow }) {
	if (row.deliveredAt === null) {
		return <span className="text-muted-foreground">{ManualLabels.list.none}</span>;
	}

	return <span className="font-mono text-foreground/80">{row.accessCount}</span>;
}

export function ManualList({
	items,
	page,
	pages,
	filter,
	onFilter,
	onPage,
	isLoading,
	isError,
}: {
	items: ManualListRow[] | undefined;
	page: number;
	pages: number;
	filter: ManualFilter;
	onFilter: (filter: ManualFilter) => void;
	onPage: (page: number) => void;
	isLoading: boolean;
	isError: boolean;
}) {
	return (
		<section className="flex flex-col gap-4">
			<div className="surface-panel overflow-hidden">
				<header className="border-b border-border px-4 py-3">
					<h2 className="type-section-title text-foreground">{ManualLabels.list.title}</h2>
				</header>

				<div className="border-b border-border px-4 py-2.5">
					<FilterSegments filter={filter} onFilter={onFilter} />
				</div>

				{isError && (
					<p className="px-4 py-12 text-center text-sm text-destructive">
						{ManualLabels.list.error}
					</p>
				)}

				{!isError && (isLoading || items === undefined) && (
					<div className="flex flex-col gap-4 p-6">
						<Skeleton className="h-10 w-full" />
						<Skeleton className="h-10 w-full" />
						<Skeleton className="h-10 w-full" />
					</div>
				)}

				{!isError && !isLoading && items !== undefined && items.length === 0 && (
					<EmptyState icon={BookHeart} title={ManualLabels.list.empty[filter].title} />
				)}

				{!isError && !isLoading && items !== undefined && items.length > 0 && (
					<ul className="flex flex-col divide-y divide-border md:hidden">
						{items.map((row) => (
							<li key={row.id} className="flex flex-col gap-2 p-4">
								<div className="flex items-center justify-between gap-2">
									<Link
										to="/clinica/manual/$id"
										params={{ id: row.id }}
										className="min-w-0 truncate font-medium text-primary transition-colors hover:text-primary-hover focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
									>
										{row.name}
									</Link>

									<StatusCell row={row} />
								</div>

								<dl className="flex flex-col gap-1 text-xs text-muted-foreground">
									<div className="flex justify-between gap-2">
										<dt>{ManualLabels.list.columnDelivered}</dt>
										<dd className="font-mono text-foreground/80">
											{format.date(row.deliveredAt) ?? ManualLabels.list.none}
										</dd>
									</div>

									<div className="flex justify-between gap-2">
										<dt>{ManualLabels.list.columnOpened}</dt>
										<dd className="min-w-0 truncate text-right">
											<OpenedCell row={row} />
										</dd>
									</div>

									<div className="flex justify-between gap-2">
										<dt>{ManualLabels.list.columnAccesses}</dt>
										<dd>
											<AccessesCell row={row} />
										</dd>
									</div>
								</dl>
							</li>
						))}
					</ul>
				)}

				{!isError && !isLoading && items !== undefined && items.length > 0 && (
					<table className="hidden w-full text-sm md:table">
						<thead>
							<tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
								<th className="px-4 py-2.5 font-medium">{ManualLabels.list.columnPatient}</th>
								<th className="px-4 py-2.5 font-medium">{ManualLabels.list.columnStatus}</th>
								<th className="px-4 py-2.5 font-medium">{ManualLabels.list.columnDelivered}</th>
								<th className="px-4 py-2.5 font-medium">{ManualLabels.list.columnOpened}</th>
								<th className="px-4 py-2.5 font-medium">{ManualLabels.list.columnAccesses}</th>
							</tr>
						</thead>
						<tbody>
							{items.map((row) => (
								<tr
									key={row.id}
									className="border-b border-border/60 transition-colors last:border-0 hover:bg-background/60"
								>
									<td className="px-4 py-2.5">
										<Link
											to="/clinica/manual/$id"
											params={{ id: row.id }}
											className="font-medium text-primary transition-colors hover:text-primary-hover focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
										>
											{row.name}
										</Link>
									</td>
									<td className="px-4 py-2.5">
										<StatusCell row={row} />
									</td>
									<td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">
										{format.date(row.deliveredAt) ?? ManualLabels.list.none}
									</td>
									<td className="px-4 py-2.5 text-xs">
										<OpenedCell row={row} />
									</td>
									<td className="px-4 py-2.5 text-xs">
										<AccessesCell row={row} />
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>

			{pages > 1 && <Pager page={page} pages={pages} onPage={onPage} />}
		</section>
	);
}
